import {
  useState,
  useCallback,
  useEffect,
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useRef,
} from "react";
import { AppState } from "react-native";
import { Auth, Hub } from "aws-amplify";
import {
  mutationAttachToIot,
  mutationAccountTimestampValidationForUnauth,
} from "src/graphql";
import { PubSub } from "@aws-amplify/pubsub";
import {
  requestUnreadCountRefreshEmit,
  pushNotificationEmit,
  LogoutEvent,
  UpdateCognitoEmailEvent,
} from "src/utils/event";
import { EventRegister } from "react-native-event-listeners";

export enum AuthState {
  Loading,
  Unauth,
  Auth,
}

type AuthType = {
  state: AuthState;
  username?: string;
  attributes?: any;
};

const AuthContext = createContext<AuthType>({
  state: AuthState.Loading,
});

export const AuthProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [cognitoAuthState, setCognitoAuthState] = useState<AuthState>(
    AuthState.Loading
  );
  const [cognitoUsername, setCognitoUsername] = useState<string | undefined>();
  const [cognitoAttributes, setCognitoCognitoAttributes] = useState<
    any | undefined
  >();
  const mqttSubscribtionRef = useRef<any>();

  const refresh = useCallback(async () => {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        setCognitoUsername(user.getUsername());
        setCognitoCognitoAttributes(user.attributes);
        setCognitoAuthState(AuthState.Auth);
      })
      .catch(() => {
        setCognitoAuthState(AuthState.Unauth);
        setCognitoUsername(undefined);
        setCognitoCognitoAttributes(undefined);
      });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const registerMQTT = useCallback(async () => {
    // Register for iot
    if (cognitoUsername) {
      const info = await Auth.currentCredentials();
      await mutationAttachToIot({ identityId: info.identityId });
    }

    if (mqttSubscribtionRef.current) {
      mqttSubscribtionRef.current?.unsubscribe();
      mqttSubscribtionRef.current = undefined;
    }

    // Subscribe to MQTT
    // FIXME: background -> active not resubscribe
    mqttSubscribtionRef.current = cognitoUsername
      ? PubSub.subscribe(cognitoUsername).subscribe({
          next: (data) => {
            console.log("mqttSubscribtion...data", data.value);
            if (data.value.apns) {
              requestUnreadCountRefreshEmit();
              pushNotificationEmit(data.value);
            } else if (data.value.pingpong) {
              console.log("get ping pong");
              // console.log("ping-pong", data.value);
              // pingEmit(data.value.pingpong);
            }
          },
          error: (error) => console.error(error),
          complete: () => console.log("Done"),
        })
      : undefined;
    return () => {
      mqttSubscribtionRef.current?.unsubscribe();
      mqttSubscribtionRef.current = undefined;
    };
  }, [cognitoUsername]);

  const unsubscribeMQTT = useCallback(async () => {
    if (mqttSubscribtionRef.current) {
      mqttSubscribtionRef.current?.unsubscribe();
      mqttSubscribtionRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    registerMQTT();
    return () => {
      unsubscribeMQTT();
    };
  }, [cognitoUsername, registerMQTT, unsubscribeMQTT]);

  const authListener = useCallback(
    (data: any) => {
      console.log("authListener", data);
      refresh();
    },
    [refresh]
  );

  useEffect(() => {
    Hub.listen("auth", authListener);
    return () => {
      Hub.remove("auth", authListener);
    };
  }, [authListener]);

  useEffect(() => {
    const updateCognitoEmailEventListener = EventRegister.addEventListener(
      UpdateCognitoEmailEvent,
      () => refresh()
    );
    return () => {
      EventRegister.removeEventListener(
        updateCognitoEmailEventListener as string
      );
    };
  }, [refresh]);

  useEffect(() => {
    const logoutEventListener = EventRegister.addEventListener(
      LogoutEvent,
      () => refresh()
    );
    return () => {
      EventRegister.removeEventListener(logoutEventListener as string);
    };
  }, [refresh]);

  // const appState = useRef(AppState.currentState);

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextAppState) => {
      if (nextAppState === "active") {
        registerMQTT();
      } else if (nextAppState.match(/inactive|background/)) {
        unsubscribeMQTT();
      }
    });

    return () => {
      subscription.remove();
    };
  }, [registerMQTT, unsubscribeMQTT]);

  return (
    <AuthContext.Provider
      value={{
        state: cognitoAuthState,
        username: cognitoUsername,
        attributes: cognitoAttributes,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const auth = useContext(AuthContext);
  return auth.state;
};

export const useSignIn = () => {
  const handler = useCallback(async (username: string, password: string) => {
    const result = await mutationAccountTimestampValidationForUnauth({
      timestamp: "" + Date.now(),
    });
    if (!result.data.accountTimestampValidation) {
      throw Error(
        "お使いのデバイスの時計がずれています。調整後に再度操作を行ってください。"
      );
    }
    await Auth.signIn(username, password);
  }, []);
  return handler;
};

export const useSignOut = () => {
  const handler = useCallback(async () => {
    const user = await Auth.currentAuthenticatedUser();
    user.signOut();
  }, []);
  return handler;
};

export const useCognito = () => {
  const auth = useContext(AuthContext);
  return [auth.username, auth.attributes] as const;
};
